import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input, OnChanges, OnDestroy,
    OnInit,
    Optional,
    Output,
    Self, SimpleChanges
} from '@angular/core';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {ControlValueAccessor, UntypedFormControl, NgControl} from '@angular/forms';
import {MatFormFieldAppearance} from '@angular/material/form-field';
import {debounceTime, distinctUntilChanged, startWith, Subject, takeUntil} from 'rxjs';

@Component({
    selector: 'pg-select',
    templateUrl: './pg-select.component.html',
    styleUrls: ['./pg-select.component.css'],
    // changeDetection: ChangeDetectionStrategy.OnPush
})
export class PgSelectComponent implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {

    @Input()
    data: any[] = [];
    @Input()
    asyncData: any | undefined;
    @Input()
    valuePath: string | undefined;
    @Input()
    titlePath: string | undefined;
    @Input()
    value: any;
    @Input()
    rawTitle: boolean = false;
    @Input()
    clearable: boolean = true;
    @Input()
    label: string = '';
    // @Input()
    // appearance: MatFormFieldAppearance = 'standard';
    @Input()
    disabled: boolean = false;
    @Output()
    valueChange: EventEmitter<any> = new EventEmitter<any>();
    @Output()
    searchUpdated: EventEmitter<any> = new EventEmitter<any>();

    search = new UntypedFormControl('');
    displayedValue = '';

    private destroyed: Subject<any> = new Subject<any>();

    public onChangeFn = (_: any) => {
    };
    public onTouchedFn = () => {
    };

    constructor(
        @Self() @Optional() public control: NgControl,
        private readonly changes: ChangeDetectorRef
    ) {
        if (this.control) {
            this.control.valueAccessor = this;
        }
        this.search.valueChanges.pipe(
            takeUntil(this.destroyed),
            startWith(null),
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((event: any) => {
            this.searchUpdated.next(event)
        })
    }

    ngOnInit(): void {
        this.changeDisplayFromValue();
    }

    ngOnDestroy() {
        this.destroyed.next(true);
        this.destroyed.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data']?.currentValue) {
            this.data = changes['data'].currentValue
            this.changeDisplayFromValue();
        }
    }

    changeDisplayFromValue() {
        const findValue = this.data.find((entry: any) => {
            if (this.valuePath) {
                return entry[this.valuePath] === this.value;
            } else {
                return JSON.stringify(entry) === JSON.stringify(this.value);
            }
        });
        if (findValue) {
            if (this.titlePath) {
                this.displayedValue = findValue[this.titlePath];
            } else {
                this.displayedValue = findValue;
            }
            this.search.setValue(this.displayedValue);
        } else {
            this.search.setValue('');
        }
    }

    clearClick(event: MouseEvent) {
        event.stopPropagation();
        this.displayedValue = '';
        this.search.setValue('');
        this.value = null;
        this.valueChange.emit(null);
        this.onChangeFn(this.value);
        this.changes.detectChanges();
    }

    focusIn(event: FocusEvent) {
        if ((event.relatedTarget as HTMLElement)?.nodeName === 'MAT-OPTION') {
            (event.target as HTMLInputElement).blur();
            return;
        }
        this.search.setValue('');
    }

    focusOut(event: FocusEvent) {
        if ((event.relatedTarget as HTMLElement)?.nodeName === 'MAT-OPTION') {
            return;
        }
        this.search.setValue(this.displayedValue);
    }

    selectOption(event: MatAutocompleteSelectedEvent) {
        this.displayedValue = event.option.viewValue;
        this.search.setValue(this.displayedValue);
        if (this.value === event.option.value) {
            return;
        }
        this.value = event.option.value;
        this.valueChange.emit(event.option.value);
        this.onChangeFn(this.value);
        this.changes.detectChanges();
    }

    getValueByPath(item: any) {
        if (!this.valuePath) {
            return item;
        }
        let result: any;
        try {
            result = item[this.valuePath];
        } catch (e) {
            console.error('INVALID VALUE PATH!', item);
        }
        return result || item;
    }

    getTitleByPath(item: any) {
        if (!this.titlePath) {
            return item;
        }
        let result: any;
        try {
            result = item[this.titlePath];
        } catch (e) {
            console.error('INVALID TITLE PATH!', item);
        }
        return result || item;
    }

    public registerOnChange(fn: any): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouchedFn = fn;
    }

    public writeValue(obj: any): void {
        this.value = obj;
        this.changeDisplayFromValue();
    }

}
